import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Alert, Chip, Button,
  Skeleton, useTheme, alpha, Tooltip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  MedicalServices as MedicalServicesIcon,
  Groups as GroupsIcon,
  Timer as TimerIcon,
  ReceiptLong as ReceiptLongIcon,
  WbSunny as WbSunnyIcon,
  NightsStay as NightsStayIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell, Tooltip as RechartsTooltip,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';

// ─── Tipos ───

interface ProfRow {
  codprofissional: number;
  nome: string;
  total_atendimentos: number;
  tempo_medio_seg: number;
  atend_com_prescricao: number;
  percentual_prescricoes: number;
}

interface TurnoTotais {
  profissionais: number;
  total_atendimentos: number;
  tempo_medio_seg: number;
  atend_com_prescricao: number;
  percentual_prescricoes: number;
}

interface Turno {
  tipo: 'atual' | 'anterior';
  rotulo: string;
  inicio: string;
  fim: string;
  totais: TurnoTotais;
  profissionais: ProfRow[];
}

interface Produtividade {
  turno_atual: Turno | null;
  turno_anterior: Turno | null;
  gerado_em: string;
}

const REFRESH_MS = 60_000;
const COR_ATUAL = '#1976d2';
const COR_ANTERIOR = '#7b1fa2';
const BAR_COLORS = ['#1976d2', '#43a047', '#ff9800', '#7b1fa2', '#00838f', '#e53935', '#6d4c41', '#546e7a'];

const num = (n?: number | null) => (n ?? 0).toLocaleString('pt-BR');
const pct = (n?: number | null) => `${(n ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

/** Formata segundos em "Xh Ym" / "Ym Zs" / "Zs". */
const formatarDuracao = (seg?: number | null): string => {
  const s = Math.max(0, Math.round(seg ?? 0));
  if (s === 0) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m}min ${r}s`;
  return `${r}s`;
};

const ehNoturno = (rotulo?: string) => /noturno/i.test(rotulo || '');

// ─── KPI compacto ───

const KpiMini: React.FC<{
  label: string; value: string; icon: React.ReactNode; color: string; loading: boolean;
}> = ({ label, value, icon, color, loading }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center',
          justifyContent: 'center', bgcolor: alpha(color, 0.12), color, flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {loading
            ? <Skeleton width={60} height={28} />
            : <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.1 }}>{value}</Typography>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Bloco de um turno ───

const TurnoBloco: React.FC<{ turno: Turno | null; destaque: boolean; loading: boolean }> = ({ turno, destaque, loading }) => {
  const theme = useTheme();
  const cor = destaque ? COR_ATUAL : COR_ANTERIOR;
  const noturno = ehNoturno(turno?.rotulo);
  const profs = turno?.profissionais || [];
  const t = turno?.totais;

  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${destaque ? alpha(cor, 0.5) : theme.palette.divider}`, height: '100%' }}>
      <CardContent>
        {/* Titulo do turno */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ color: cor, display: 'flex' }}>{noturno ? <NightsStayIcon /> : <WbSunnyIcon />}</Box>
          <Typography variant="h6" fontWeight={700}>{destaque ? 'Turno Atual' : 'Turno Anterior'}</Typography>
          <Chip size="small" label={turno?.rotulo || (noturno ? 'Noturno (19h-07h)' : 'Diurno (07h-19h)')}
            sx={{ bgcolor: alpha(cor, 0.12), color: cor, fontWeight: 600 }} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {turno ? <>{turno.inicio} &nbsp;→&nbsp; {turno.fim}</> : '—'}
        </Typography>

        {/* KPIs do turno */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}><KpiMini label="Profissionais" value={num(t?.profissionais)} icon={<MedicalServicesIcon fontSize="small" />} color="#00838f" loading={loading} /></Grid>
          <Grid item xs={6} md={3}><KpiMini label="Atendimentos" value={num(t?.total_atendimentos)} icon={<GroupsIcon fontSize="small" />} color={cor} loading={loading} /></Grid>
          <Grid item xs={6} md={3}><KpiMini label="Tempo medio" value={formatarDuracao(t?.tempo_medio_seg)} icon={<TimerIcon fontSize="small" />} color="#ff9800" loading={loading} /></Grid>
          <Grid item xs={6} md={3}><KpiMini label="% prescricoes" value={pct(t?.percentual_prescricoes)} icon={<ReceiptLongIcon fontSize="small" />} color="#43a047" loading={loading} /></Grid>
        </Grid>

        {/* Grafico de atendimentos por profissional */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Atendimentos por profissional</Typography>
        {loading ? <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 1 }} /> : (profs.length ? (
          <ResponsiveContainer width="100%" height={Math.max(180, Math.min(profs.length, 12) * 34)}>
            <BarChart data={profs.slice(0, 12)} layout="vertical" margin={{ top: 4, right: 36, bottom: 4, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={alpha(theme.palette.text.primary, 0.08)} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="nome" width={150} tick={{ fontSize: 10 }} interval={0} />
              <RechartsTooltip formatter={(v: any) => [num(Number(v)), 'Atendimentos']} />
              <Bar dataKey="total_atendimentos" name="Atendimentos" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 11 }}>
                {profs.slice(0, 12).map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : <Box sx={{ py: 5, textAlign: 'center' }}><Typography color="text.secondary">Sem atendimentos neste turno.</Typography></Box>)}

        {/* Tabela por profissional */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>Detalhamento por profissional</Typography>
        {loading ? <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1 }} /> : (profs.length ? (
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Profissional</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Atendimentos</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Tempo medio</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>% prescricoes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profs.map((p) => (
                  <TableRow key={p.codprofissional} hover>
                    <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.nome}</TableCell>
                    <TableCell align="center">{num(p.total_atendimentos)}</TableCell>
                    <TableCell align="right">{formatarDuracao(p.tempo_medio_seg)}</TableCell>
                    <TableCell align="right">{pct(p.percentual_prescricoes)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : <Box sx={{ py: 3, textAlign: 'center' }}><Typography color="text.secondary" variant="body2">Nenhum profissional com atendimento neste turno.</Typography></Box>)}
      </CardContent>
    </Card>
  );
};

// ─── Pagina ───

const SaudeProdutividade: React.FC = () => {
  const theme = useTheme();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const [data, setData] = useState<Produtividade | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date | null>(null);
  const [segundos, setSegundos] = useState(REFRESH_MS / 1000);

  const fetchRef = useRef<() => void>(() => {});

  const fetchData = useCallback(async (silencioso = false) => {
    if (!municipioAtivo?.id) return;
    if (!silencioso) setLoading(true);
    setError(null);
    try {
      const result = await saudeService.getProdutividade(municipioAtivo.id, {
        unidade: unidadeSelecionada?.codunidade,
      });
      setData(result);
      setUltimaAtualizacao(new Date());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar a produtividade.');
    } finally {
      setLoading(false);
      setSegundos(REFRESH_MS / 1000);
    }
  }, [municipioAtivo?.id, unidadeSelecionada?.codunidade]);

  useEffect(() => { fetchRef.current = () => fetchData(true); });

  // Busca inicial / ao trocar municipio ou unidade
  useEffect(() => { fetchData(false); }, [fetchData]);

  // Auto-refresh a cada 60s (silencioso) + contador regressivo
  useEffect(() => {
    if (!municipioAtivo?.id) return;
    const refresh = setInterval(() => fetchRef.current(), REFRESH_MS);
    const tick = setInterval(() => setSegundos((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [municipioAtivo?.id]);

  if (!municipioAtivo?.id) {
    return <Box sx={{ p: 3 }}><Alert severity="info">Selecione um municipio para visualizar a produtividade.</Alert></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Cabecalho */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MedicalServicesIcon color="primary" /> Produtividade por Turno
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {unidadeSelecionada
              ? <>Unidade: <strong>{unidadeSelecionada.nome}</strong></>
              : 'Todas as unidades do municipio'}
            {' • Turno atual e anterior (Diurno 07h-19h / Noturno 19h-07h) — atendimentos, tempo medio e % de prescricoes por profissional'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {ultimaAtualizacao ? `Atualizado as ${ultimaAtualizacao.toLocaleTimeString('pt-BR')}` : 'Carregando...'}
            </Typography>
            <Typography variant="caption" color="text.disabled">Proxima atualizacao em {segundos}s</Typography>
          </Box>
          <Tooltip title="Atualizar agora">
            <span>
              <IconButton onClick={() => fetchData(false)} disabled={loading} color="primary"
                sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => fetchData(false)}>Tentar novamente</Button>}>{error}</Alert>}

      {/* Dois turnos lado a lado */}
      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <TurnoBloco turno={data?.turno_atual ?? null} destaque loading={loading} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <TurnoBloco turno={data?.turno_anterior ?? null} destaque={false} loading={loading} />
        </Grid>
      </Grid>

      <Chip size="small" variant="outlined" sx={{ mt: 2 }}
        label="Tempo medio = media de (fim - inicio) dos atendimentos finalizados • % prescricoes = atendimentos com prescricao / total" />
    </Box>
  );
};

export default SaudeProdutividade;
